// Client/src/partner/PartnerAdmin.js
//
// Astoval Partner Admin v0.4
// A closed operational screen for creating, archiving, deleting and crediting
// reseller organisations. It uses the existing Supabase browser session only; no
// service-role key, Partner API key or Stripe secret is ever exposed here.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  hasPartnerWorkspaceSupabaseConfig,
  partnerWorkspaceSupabase,
} from "./supabaseClient";

const SUPPORT_EMAIL = "support@astoval.com";

const shellStyle = {
  minHeight: "100vh",
  boxSizing: "border-box",
  padding: "32px 18px",
  background: "#f5f7fb",
  color: "#101828",
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const panelStyle = {
  width: "min(100%, 980px)",
  margin: "0 auto",
  overflow: "hidden",
  border: "1px solid #e4e7ec",
  borderRadius: 16,
  background: "#ffffff",
  boxShadow: "0 14px 35px rgba(16, 24, 40, 0.08)",
};

const primaryButtonStyle = {
  appearance: "none",
  border: 0,
  borderRadius: 8,
  background: "#ff7a00",
  color: "#111827",
  padding: "11px 14px",
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  ...primaryButtonStyle,
  border: "1px solid #d0d5dd",
  background: "#ffffff",
  color: "#344054",
};

const dangerButtonStyle = {
  ...secondaryButtonStyle,
  border: "1px solid #fda29b",
  color: "#b42318",
  background: "#ffffff",
};

const inputStyle = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #d0d5dd",
  borderRadius: 8,
  padding: "10px 11px",
  color: "#101828",
  background: "#ffffff",
  fontSize: 14,
};

const labelStyle = {
  display: "block",
  marginBottom: 6,
  color: "#344054",
  fontSize: 13,
  fontWeight: 750,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
};

const initialPartnerForm = {
  slug: "",
  legalName: "",
  displayName: "",
  ownerName: "",
  ownerEmail: "",
  initialCredits: "0",
  partnerReportName: "",
  primaryColor: "#173f5f",
  accentColor: "#00a6a6",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function buildAdminRedirectUrl() {
  try {
    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set("workspace", "partner-admin");
    return url.toString();
  } catch {
    return "https://tokenlex-ui-dev.onrender.com/?workspace=partner-admin";
  }
}

function formatTime(value) {
  const timestamp = Date.parse(String(value || ""));
  if (!Number.isFinite(timestamp)) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toLocaleString();
  }
}

function readableAdminError(response, payload) {
  if (response.status === 401) {
    return "Your secure session has expired. Please sign in again.";
  }
  if (response.status === 403) {
    return "This account is not authorized for Astoval Partner Admin.";
  }
  if (payload?.message) return String(payload.message);
  return "Partner Admin is temporarily unavailable. Please try again shortly.";
}

async function adminFetch(apiBase, session, path, options = {}) {
  const response = await fetch(`${apiBase}/api/partner/admin${path}`, {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${session.access_token}`,
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.ok) {
    throw new Error(readableAdminError(response, payload));
  }

  return payload;
}

function StatusPill({ status }) {
  const normalized = String(status || "").toLowerCase();
  const config =
    normalized === "active"
      ? { background: "#ecfdf3", border: "#abefc6", color: "#05603a", label: "Active" }
      : normalized === "suspended"
        ? { background: "#f2f4f7", border: "#d0d5dd", color: "#344054", label: "Archived" }
        : { background: "#fffaeb", border: "#fedf89", color: "#b54708", label: normalized || "Unknown" };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        border: `1px solid ${config.border}`,
        borderRadius: 999,
        padding: "4px 8px",
        background: config.background,
        color: config.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {config.label}
    </span>
  );
}

function PartnerCard({
  partner,
  session,
  apiBase,
  onChanged,
}) {
  const [creditCount, setCreditCount] = useState("1");
  const [creditNote, setCreditNote] = useState("");
  const [working, setWorking] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function sendAccess() {
    const ownerEmail = String(partner.owner?.email || "").trim();
    if (!ownerEmail) {
      setError("No owner email is available for this partner.");
      return;
    }

    const confirmed = window.confirm(
      `This will immediately send a secure TVAC Partner Workspace access email to ${ownerEmail}.\n\nOnly continue after you have informed the recipient. Send the email now?`
    );
    if (!confirmed) return;

    setWorking("access");
    setNotice("");
    setError("");
    try {
      const payload = await adminFetch(apiBase, session, `/partners/${partner.id}/send-access`, {
        method: "POST",
      });
      setNotice(`Secure ${payload.delivery === "invite" ? "invite" : "sign-in"} email sent to ${payload.owner_email}.`);
      await onChanged();
    } catch (requestError) {
      setError(requestError?.message || "Access email could not be sent.");
    } finally {
      setWorking("");
    }
  }

  async function updateStatus() {
    const nextStatus = partner.status === "active" ? "suspended" : "active";
    const isArchiving = nextStatus === "suspended";
    const confirmed = window.confirm(
      isArchiving
        ? `Archive ${partner.display_name}?

Workspace access will be disabled, while reports, payments, credits and audit history are retained.`
        : `Reactivate ${partner.display_name}?

Workspace access and normal partner operations will be restored.`
    );
    if (!confirmed) return;

    setWorking("status");
    setNotice("");
    setError("");
    try {
      await adminFetch(apiBase, session, `/partners/${partner.id}/status`, {
        method: "PATCH",
        body: { status: nextStatus },
      });
      setNotice(`Partner ${nextStatus === "active" ? "reactivated" : "archived"}.`);
      await onChanged();
    } catch (requestError) {
      setError(requestError?.message || "Partner status could not be updated.");
    } finally {
      setWorking("");
    }
  }

  async function deletePermanently() {
    const safety = partner.deletion_safety || {};
    const blockers = Array.isArray(safety.blockers) ? safety.blockers : [];

    if (partner.status !== "suspended") {
      setError("Archive this partner before permanent deletion.");
      return;
    }
    if (!safety.can_delete_permanently) {
      setError(
        blockers.length
          ? `Permanent deletion is blocked: ${blockers.join(" ")}`
          : "Permanent deletion is blocked because protected records were found."
      );
      return;
    }

    const assessmentCount = Number(safety.assessment_count || 0);
    const testPurchaseCount = Number(safety.test_stripe_purchase_count || 0);
    const apiKeyCount = Number(safety.api_key_count || 0);
    const memberCount = Number(safety.member_count || 0);

    const confirmed = window.confirm(
      `Permanently delete ${partner.display_name}?

This deletes the partner workspace and all partner-scoped data, including ${assessmentCount} assessment record(s), private report files, credits, ${testPurchaseCount} Stripe test record(s), ${apiKeyCount} API key(s), branding and ${memberCount} membership record(s).

This cannot be undone. Supabase Auth users are retained but lose access when the partner membership is deleted.`
    );
    if (!confirmed) return;

    const typedSlug = window.prompt(
      `Final confirmation: type the exact partner slug below.

${partner.slug}`,
      ""
    );
    if (typedSlug === null) return;
    if (typedSlug.trim() !== partner.slug) {
      setError(`Deletion cancelled. The confirmation must exactly match “${partner.slug}”.`);
      return;
    }

    setWorking("delete");
    setNotice("");
    setError("");
    try {
      const payload = await adminFetch(apiBase, session, `/partners/${partner.id}`, {
        method: "DELETE",
        body: { confirm_slug: typedSlug.trim() },
      });
      const deletedName = payload.deleted_partner?.display_name || partner.display_name;
      const warning = payload.storage_cleanup_warning ? `

Warning: ${payload.storage_cleanup_warning}` : "";
      window.alert(`${deletedName} and its partner data were permanently deleted.${warning}`);
      await onChanged();
    } catch (requestError) {
      setError(requestError?.message || "The partner could not be deleted safely.");
    } finally {
      setWorking("");
    }
  }

  async function grantCredits(event) {
    event.preventDefault();
    const quantity = Number(creditCount);

    if (!Number.isInteger(quantity) || quantity < 1) {
      setError("Enter a whole credit quantity of at least 1.");
      return;
    }

    setWorking("credits");
    setNotice("");
    setError("");
    try {
      const payload = await adminFetch(apiBase, session, `/partners/${partner.id}/credits`, {
        method: "POST",
        body: {
          credits: quantity,
          note: creditNote,
        },
      });
      setNotice(`${payload.credit_grant.credits_granted} manual credit(s) granted and recorded.`);
      setCreditCount("1");
      setCreditNote("");
      await onChanged();
    } catch (requestError) {
      setError(requestError?.message || "Credits could not be granted.");
    } finally {
      setWorking("");
    }
  }

  const owner = partner.owner;
  const credits = partner.credits || {};
  const deletionSafety = partner.deletion_safety || {};
  const deletionBlockers = Array.isArray(deletionSafety.blockers) ? deletionSafety.blockers : [];
  const canDeletePermanently = Boolean(deletionSafety.can_delete_permanently);
  const recentLots = Array.isArray(partner.recent_credit_lots) ? partner.recent_credit_lots : [];

  return (
    <article
      style={{
        marginTop: 16,
        padding: 18,
        border: "1px solid #eaecf0",
        borderRadius: 12,
        background: "#ffffff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>{partner.display_name}</h3>
            <StatusPill status={partner.status} />
          </div>
          <p style={{ margin: "6px 0 0", color: "#667085", fontSize: 13 }}>
            {partner.legal_name} · <code>{partner.slug}</code>
          </p>
        </div>
        <div style={{ color: "#667085", fontSize: 12, textAlign: "right" }}>
          Created<br />
          <strong style={{ color: "#344054" }}>{formatTime(partner.created_at)}</strong>
        </div>
      </div>

      <div style={{ ...gridStyle, marginTop: 16 }}>
        <div style={{ padding: 13, borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa" }}>
          <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Partner credits
          </div>
          <div style={{ marginTop: 5, fontSize: 27, fontWeight: 900 }}>{Number(credits.available || 0)}</div>
          <div style={{ marginTop: 3, fontSize: 12, color: "#7c2d12" }}>
            {Number(credits.total_remaining || 0)} total remaining · {Number(credits.reserved || 0)} reserved
          </div>
        </div>

        <div style={{ padding: 13, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div style={{ color: "#475467", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Owner
          </div>
          <div style={{ marginTop: 6, color: "#101828", fontWeight: 750 }}>{owner?.display_name || "—"}</div>
          <div style={{ marginTop: 3, color: "#475467", fontSize: 13 }}>{owner?.email || "—"}</div>
          <div style={{ marginTop: 5, color: owner?.has_auth_user ? "#027a48" : "#b54708", fontSize: 12, fontWeight: 700 }}>
            {owner?.has_auth_user ? "Secure access enabled" : "Access email not sent"}
          </div>
        </div>

        <div style={{ padding: 13, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div style={{ color: "#475467", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Branding
          </div>
          <div style={{ marginTop: 6, color: "#101828", fontWeight: 750 }}>
            {partner.branding?.partner_name || "Not configured"}
          </div>
          <div style={{ marginTop: 5, color: partner.branding?.is_locked ? "#b54708" : "#027a48", fontSize: 12, fontWeight: 700 }}>
            {partner.branding?.is_locked ? "Locked" : "Self-service enabled"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
        <button
          type="button"
          style={secondaryButtonStyle}
          disabled={Boolean(working) || !owner?.email || partner.status !== "active"}
          onClick={() => void sendAccess()}
        >
          {working === "access"
            ? "Sending…"
            : owner?.has_auth_user
              ? "Send new access email"
              : "Send access email"}
        </button>
        <button type="button" style={secondaryButtonStyle} disabled={Boolean(working)} onClick={() => void updateStatus()}>
          {working === "status"
            ? "Updating…"
            : partner.status === "active"
              ? "Archive partner"
              : "Reactivate partner"}
        </button>
      </div>
      {partner.status !== "active" ? (
        <div style={{ marginTop: 8, color: "#667085", fontSize: 12 }}>
          This partner is archived. Workspace access, access-email sending and new credit grants are disabled, while historical data is retained.
        </div>
      ) : null}

      <form
        onSubmit={grantCredits}
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: "1px solid #eaecf0",
        }}
      >
        <div style={{ color: "#344054", fontSize: 14, fontWeight: 800 }}>Grant manual / beta credits</div>
        <div style={{ ...gridStyle, marginTop: 10 }}>
          <label>
            <span style={labelStyle}>Credits</span>
            <input
              type="number"
              min="1"
              step="1"
              value={creditCount}
              onChange={(event) => setCreditCount(event.target.value)}
              style={inputStyle}
            />
          </label>
          <label>
            <span style={labelStyle}>Internal note</span>
            <input
              type="text"
              maxLength="800"
              value={creditNote}
              onChange={(event) => setCreditNote(event.target.value)}
              placeholder="e.g. Closed beta allocation, signed 24 June"
              style={inputStyle}
            />
          </label>
        </div>
        <button type="submit" disabled={Boolean(working) || partner.status !== "active"} style={{ ...primaryButtonStyle, marginTop: 12, opacity: partner.status === "active" ? 1 : 0.55 }}>
          {working === "credits" ? "Granting…" : "Grant credits"}
        </button>
        {partner.status !== "active" ? (
          <div style={{ marginTop: 8, color: "#b54708", fontSize: 12 }}>
            Reactivate this partner before granting credits.
          </div>
        ) : null}
      </form>

      {recentLots.length ? (
        <div style={{ marginTop: 16, fontSize: 12, color: "#667085" }}>
          <strong style={{ color: "#344054" }}>Recent credit activity:</strong>
          <ul style={{ margin: "7px 0 0", paddingLeft: 18 }}>
            {recentLots.map((lot) => (
              <li key={lot.id} style={{ marginTop: 3 }}>
                {lot.source} · {lot.credits_total} credits · {lot.status} · {formatTime(lot.created_at)}
                {lot.notes ? ` · ${lot.notes}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: "1px solid #eaecf0",
        }}
      >
        <div style={{ color: "#b42318", fontSize: 14, fontWeight: 850 }}>Permanent deletion</div>
        <p style={{ margin: "6px 0 0", color: "#667085", fontSize: 12, lineHeight: 1.5 }}>
          Permanent deletion removes the archived partner and all partner-scoped data, including assessments, reports, credits, Stripe test records, API keys, branding and memberships. Live Stripe purchase history, reserved credits and queued or running assessments remain protected.
        </p>
        {deletionBlockers.length ? (
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#7a271a", fontSize: 12, lineHeight: 1.5 }}>
            {deletionBlockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
          </ul>
        ) : (
          <div style={{ marginTop: 8, color: "#027a48", fontSize: 12, fontWeight: 700 }}>
            This archived partner has no protected blockers. Its assessments and other partner-scoped data will be deleted with the workspace.
          </div>
        )}
        <button
          type="button"
          style={{ ...dangerButtonStyle, marginTop: 10, opacity: canDeletePermanently ? 1 : 0.5 }}
          disabled={Boolean(working) || !canDeletePermanently}
          onClick={() => void deletePermanently()}
        >
          {working === "delete" ? "Deleting…" : "Delete permanently"}
        </button>
      </div>

      {notice ? <div role="status" style={{ marginTop: 14, color: "#027a48", lineHeight: 1.5 }}>{notice}</div> : null}
      {error ? <div role="alert" style={{ marginTop: 14, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}
    </article>
  );
}

function PartnerAdmin({ apiBase }) {
  const [session, setSession] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [email, setEmail] = useState("");
  const [admin, setAdmin] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [form, setForm] = useState(initialPartnerForm);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const authenticated = Boolean(session?.access_token && admin);

  const activePartners = useMemo(
    () => partners.filter((partner) => partner.status === "active").length,
    [partners]
  );

  const loadDashboard = useCallback(
    async (activeSession) => {
      if (!activeSession?.access_token) {
        setAdmin(null);
        setPartners([]);
        return;
      }

      setLoadingDashboard(true);
      setError("");

      try {
        const me = await adminFetch(apiBase, activeSession, "/me");
        const list = await adminFetch(apiBase, activeSession, "/partners");
        setAdmin(me.admin || null);
        setPartners(Array.isArray(list.partners) ? list.partners : []);
      } catch (loadError) {
        setAdmin(null);
        setPartners([]);
        setError(loadError?.message || "Partner Admin could not be loaded.");
      } finally {
        setLoadingDashboard(false);
      }
    },
    [apiBase]
  );

  useEffect(() => {
    if (!hasPartnerWorkspaceSupabaseConfig || !partnerWorkspaceSupabase) {
      setInitializing(false);
      return undefined;
    }

    let active = true;

    async function restoreSession() {
      const { data, error: sessionError } = await partnerWorkspaceSupabase.auth.getSession();
      if (!active) return;

      if (sessionError) {
        setError("Could not restore your secure session. Please request a new sign-in link.");
      }

      const restored = data?.session || null;
      setSession(restored);
      setInitializing(false);
      if (restored) await loadDashboard(restored);
    }

    void restoreSession();

    const {
      data: { subscription },
    } = partnerWorkspaceSupabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;

      const next = nextSession || null;
      setSession(next);

      if (event === "SIGNED_OUT") {
        setAdmin(null);
        setPartners([]);
        return;
      }

      if (next && ["INITIAL_SESSION", "SIGNED_IN", "TOKEN_REFRESHED"].includes(event)) {
        window.setTimeout(() => {
          if (active) void loadDashboard(next);
        }, 0);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [loadDashboard]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setNotice("");
    setError("");
  }

  async function handleMagicLinkRequest(event) {
    event.preventDefault();
    const normalizedEmail = String(email || "").trim().toLowerCase();

    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid Astoval work email.");
      return;
    }

    setSendingLink(true);
    setNotice("");
    setError("");

    try {
      const { error: signInError } = await partnerWorkspaceSupabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: buildAdminRedirectUrl(),
        },
      });

      if (signInError) throw signInError;
      setNotice("If this address is an approved Astoval staff account, a secure sign-in link is on its way.");
    } catch (signInError) {
      console.warn("Partner Admin magic-link request failed:", signInError?.message || signInError);
      setError(`A secure sign-in link could not be sent. Please try again or contact ${SUPPORT_EMAIL}.`);
    } finally {
      setSendingLink(false);
    }
  }

  async function handleCreatePartner(event) {
    event.preventDefault();

    const initialCredits = Number(form.initialCredits);
    if (!Number.isInteger(initialCredits) || initialCredits < 0 || initialCredits > 10000) {
      setError("Initial credits must be a whole number from 0 to 10,000.");
      return;
    }
    if (!form.slug.trim() || !form.legalName.trim() || !form.displayName.trim() || !form.ownerName.trim() || !isValidEmail(form.ownerEmail)) {
      setError("Complete the partner slug, legal name, display name, owner name and owner email.");
      return;
    }

    setCreating(true);
    setNotice("");
    setError("");

    try {
      const payload = await adminFetch(apiBase, session, "/partners", {
        method: "POST",
        body: {
          slug: form.slug,
          legal_name: form.legalName,
          display_name: form.displayName,
          owner_name: form.ownerName,
          owner_email: form.ownerEmail,
          initial_credits: initialCredits,
          partner_report_name: form.partnerReportName || form.displayName,
          primary_color: form.primaryColor,
          accent_color: form.accentColor,
          branding_locked: false,
        },
      });

      setNotice(
        `Partner ${payload.partner.display_name} created. No email was sent. ` +
          `Contact the prospective partner first, then use “Send access email” in the partner card when you are ready.`
      );
      setForm(initialPartnerForm);
      await loadDashboard(session);
    } catch (createError) {
      setError(createError?.message || "Partner could not be created.");
    } finally {
      setCreating(false);
    }
  }

  async function handleSignOut() {
    await partnerWorkspaceSupabase.auth.signOut();
    setSession(null);
    setAdmin(null);
    setPartners([]);
    setNotice("");
    setError("");
  }

  if (initializing) {
    return <div style={shellStyle}>Loading secure Partner Admin…</div>;
  }

  if (!hasPartnerWorkspaceSupabaseConfig || !partnerWorkspaceSupabase) {
    return (
      <div style={shellStyle}>
        <div style={{ ...panelStyle, padding: 28 }}>
          <h1 style={{ marginTop: 0 }}>Partner Admin configuration required</h1>
          <p style={{ color: "#475467", lineHeight: 1.55 }}>
            Supabase browser configuration is missing. Partner Admin cannot start without the existing workspace sign-in configuration.
          </p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={shellStyle}>
        <div style={{ ...panelStyle, maxWidth: 560 }}>
          <div style={{ padding: 28 }}>
            <div style={{ color: "#ff7a00", fontWeight: 900, letterSpacing: "0.08em" }}>TVAC</div>
            <h1 style={{ margin: "12px 0 8px", fontSize: 28 }}>Astoval Partner Admin</h1>
            <p style={{ margin: 0, color: "#475467", lineHeight: 1.55 }}>
              Closed operational area for approved Astoval staff only.
            </p>

            <form onSubmit={handleMagicLinkRequest} style={{ marginTop: 22 }}>
              <label htmlFor="partner-admin-email" style={labelStyle}>Astoval work email</label>
              <input
                id="partner-admin-email"
                type="email"
                autoComplete="email"
                placeholder="name@astoval.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                style={{ ...inputStyle, fontSize: 16 }}
              />
              <button type="submit" disabled={sendingLink || !email.trim()} style={{ ...primaryButtonStyle, marginTop: 14, opacity: sendingLink || !email.trim() ? 0.6 : 1 }}>
                {sendingLink ? "Sending secure link…" : "Send secure sign-in link"}
              </button>
            </form>

            {notice ? <div role="status" style={{ marginTop: 16, color: "#027a48", lineHeight: 1.5 }}>{notice}</div> : null}
            {error ? <div role="alert" style={{ marginTop: 16, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={panelStyle}>
        <header
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #eaecf0",
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ color: "#ff7a00", fontWeight: 900, letterSpacing: "0.08em" }}>TVAC</div>
            <div style={{ marginTop: 4, color: "#667085", fontSize: 13 }}>Astoval Partner Admin v0.4</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: "#475467", fontSize: 13 }}>{admin?.email}</span>
            <button type="button" onClick={() => void handleSignOut()} style={secondaryButtonStyle}>Sign out</button>
          </div>
        </header>

        <main style={{ padding: 24 }}>
          <section
            style={{
              padding: 18,
              borderRadius: 12,
              background: "#fff7ed",
              border: "1px solid #fed7aa",
            }}
          >
            <div style={{ color: "#9a3412", fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Partner estate
            </div>
            <div style={{ marginTop: 6, fontSize: 28, fontWeight: 900 }}>{partners.length}</div>
            <div style={{ marginTop: 3, color: "#7c2d12", fontSize: 13 }}>
              {activePartners} active partner{activePartners === 1 ? "" : "s"} · {partners.length - activePartners} archived
            </div>
          </section>

          <section
            style={{
              marginTop: 20,
              padding: 20,
              borderRadius: 12,
              border: "1px solid #eaecf0",
              background: "#ffffff",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 21 }}>Create partner</h2>
            <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5 }}>
              Creates an isolated workspace record for a prospective or confirmed reseller partner, its first owner, a self-service co-branded profile and optional beta credits. API access remains disabled.
            </p>
            <div
              style={{
                marginTop: 14,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #fedf89",
                background: "#fffaeb",
                color: "#7a2e0e",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <strong>No email is sent during creation.</strong> Contact the prospective partner first. When you are ready, use the separate “Send access email” button in the partner card below.
            </div>

            <form onSubmit={handleCreatePartner} style={{ marginTop: 18 }}>
              <div style={gridStyle}>
                <label>
                  <span style={labelStyle}>Partner slug *</span>
                  <input value={form.slug} onChange={(event) => updateForm("slug", event.target.value)} placeholder="example-advisory" style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Legal company name *</span>
                  <input value={form.legalName} onChange={(event) => updateForm("legalName", event.target.value)} placeholder="Example Advisory GmbH" style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Partner display name *</span>
                  <input value={form.displayName} onChange={(event) => updateForm("displayName", event.target.value)} placeholder="Example Advisory" style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Initial credits</span>
                  <input type="number" min="0" step="1" value={form.initialCredits} onChange={(event) => updateForm("initialCredits", event.target.value)} style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Owner name *</span>
                  <input value={form.ownerName} onChange={(event) => updateForm("ownerName", event.target.value)} placeholder="Jane Doe" style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Owner email *</span>
                  <input type="email" value={form.ownerEmail} onChange={(event) => updateForm("ownerEmail", event.target.value)} placeholder="jane@example.com" style={inputStyle} />
                </label>
              </div>

              <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #eaecf0" }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Initial co-branding</h3>
                <p style={{ margin: "5px 0 14px", color: "#667085", fontSize: 13 }}>
                  The partner can later update its own logo, colors and report-contact details in Partner Workspace.
                </p>
                <div style={gridStyle}>
                  <label>
                    <span style={labelStyle}>Partner report name *</span>
                    <input value={form.partnerReportName} onChange={(event) => updateForm("partnerReportName", event.target.value)} placeholder={form.displayName || "Example Advisory"} style={inputStyle} />
                  </label>
                  <label>
                    <span style={labelStyle}>Primary color</span>
                    <input value={form.primaryColor} onChange={(event) => updateForm("primaryColor", event.target.value)} placeholder="#173f5f" style={inputStyle} />
                  </label>
                  <label>
                    <span style={labelStyle}>Accent color</span>
                    <input value={form.accentColor} onChange={(event) => updateForm("accentColor", event.target.value)} placeholder="#00a6a6" style={inputStyle} />
                  </label>
                </div>
              </div>

              <button type="submit" disabled={creating} style={{ ...primaryButtonStyle, marginTop: 20, opacity: creating ? 0.65 : 1 }}>
                {creating ? "Creating partner…" : "Create partner without sending email"}
              </button>
            </form>
          </section>

          <section style={{ marginTop: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 21 }}>Partners</h2>
                <p style={{ margin: "7px 0 0", color: "#475467", lineHeight: 1.5 }}>
                  Credits, ownership, access, archive status and controlled deletion.
                </p>
              </div>
              <button type="button" onClick={() => void loadDashboard(session)} disabled={loadingDashboard} style={secondaryButtonStyle}>
                {loadingDashboard ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            {loadingDashboard ? <p style={{ marginTop: 16, color: "#667085" }}>Loading partners…</p> : null}
            {!loadingDashboard && partners.length === 0 ? <p style={{ marginTop: 16, color: "#667085" }}>No partner organizations have been created yet.</p> : null}

            {partners.map((partner) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                session={session}
                apiBase={apiBase}
                onChanged={() => loadDashboard(session)}
              />
            ))}
          </section>

          <section
            style={{
              marginTop: 24,
              padding: 16,
              borderRadius: 12,
              background: "#f9fafb",
              border: "1px solid #eaecf0",
              color: "#475467",
              lineHeight: 1.55,
              fontSize: 13,
            }}
          >
            <strong style={{ color: "#344054" }}>Operational controls:</strong> create a workspace without contacting the recipient, send access separately, archive or reactivate a partner, grant documented manual credits, and permanently delete an archived workspace together with its assessments and test data. Live Stripe purchase history, reserved credits and in-progress assessments remain protected.
          </section>

          {notice ? <div role="status" style={{ marginTop: 18, color: "#027a48", lineHeight: 1.5 }}>{notice}</div> : null}
          {error ? <div role="alert" style={{ marginTop: 18, color: "#b42318", lineHeight: 1.5 }}>{error}</div> : null}
        </main>
      </div>
    </div>
  );
}

export default PartnerAdmin;
